/* ===========================================================================
 * home-uplift.js — scroll-in reveals for the home page
 *
 * Loaded from layout/theme.liquid for the index template only.
 *
 * Deliberately additive: it selects elements the Pipeline sections already
 * render and adds classes to them. No section Liquid is modified, so a theme
 * update can't silently drop the animation — at worst a selector stops
 * matching and that element simply doesn't animate.
 *
 * Fail-open: home-uplift.css hides an element only once this script has added
 * .hu-watch to it. If the script never runs, nothing is ever hidden.
 *
 * Elements already animated by the theme's own AOS (the icon columns carry
 * data-aos="svg-dash") are left alone rather than animated twice.
 * ======================================================================== */
(function () {
  'use strict';

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) {}

  /* Each entry is one reveal group.
   *   within  — the section container to search inside (optional)
   *   select  — what actually animates
   *   stagger — index siblings so they cascade instead of arriving together
   *
   * The product carousel is revealed as a single unit rather than per card:
   * the cards live inside Flickity, and animating cells individually risks
   * interfering with its drag and cell measurement. */
  var TARGETS = [
    { select: '.boxes-grid .box', stagger: true },
    { select: '.section-collection-tabs .collection-tabs', stagger: false },
    { select: '.brick__section .brick__block', stagger: true },
    { select: '.section-logos .logo-bar__wrapper', stagger: false }
  ];

  function collect() {
    var groups = [];

    TARGETS.forEach(function (target) {
      var nodes = document.querySelectorAll(target.select);
      if (!nodes.length) return;

      // Stagger restarts per parent, so two separate brick rows each cascade
      // from zero rather than the second row inheriting the first row's offset.
      var counters = new Map();

      Array.prototype.forEach.call(nodes, function (node) {
        if (node.dataset.huWatch === 'true') return;

        var index = 0;
        if (target.stagger) {
          var parent = node.parentElement;
          index = counters.get(parent) || 0;
          counters.set(parent, index + 1);
        }

        node.dataset.huWatch = 'true';
        node.style.setProperty('--hu-i', String(index));
        groups.push(node);
      });
    });

    return groups;
  }

  function reveal(node) {
    node.classList.add('hu-in');
  }

  /* layout/theme.liquid hid the targets before the first paint, so they never
   * flash in and back out, and holds a timer that shows them again in case this
   * script never arrives. Settle that on the first run only: `take` when this
   * script is going to do the revealing, `drop` to show them right now if it
   * isn't. Later runs (the theme editor re-rendering a section) must not touch
   * it — by then collect() legitimately returns nothing, and dropping would
   * un-hide everything still waiting to be scrolled to. */
  var settled = false;

  function settle(how) {
    if (settled) return;
    settled = true;
    if (window.themeReveal) window.themeReveal[how]('hu');
  }

  function init() {
    /* Bail before touching anything in every case where the reveal might
     * not run:
     *   - motion reduced, so there should be no reveal at all;
     *   - no IntersectionObserver to trigger it;
     *   - a hidden tab ("open in new tab", prerender), which is served no
     *     animation frames, so the reveal would strand content at opacity 0.
     * Bailing here leaves the page in its plain, fully visible state — the
     * cost is a missed animation, never missing content. */
    if (
      REDUCED ||
      !('IntersectionObserver' in window) ||
      document.visibilityState === 'hidden'
    ) {
      settle('drop');
      return;
    }

    var nodes = collect();
    if (!nodes.length) {
      settle('drop');
      return;
    }

    settle('take');

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          obs.unobserve(entry.target); // reveal once, then stop watching
        });
      },
      /* threshold 0 — fire on the first pixel. A non-zero threshold can never
       * be met by an element taller than the viewport sitting near the end of
       * the page, which would strand it at opacity 0. The negative bottom
       * rootMargin already holds the reveal back until the element is properly
       * on screen, so the threshold isn't needed for timing. */
      { rootMargin: '0px 0px -12% 0px', threshold: 0 }
    );

    nodes.forEach(function (node) {
      var rect = node.getBoundingClientRect();
      var alreadyVisible = rect.top < (window.innerHeight || 0) && rect.bottom > 0;

      node.classList.add('hu-watch');

      // Anything already on screen at load reveals on the next frame, so the
      // above-the-fold content animates in rather than sitting hidden until
      // the first scroll.
      if (alreadyVisible) {
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            reveal(node);
          });
        });
        // Guarantee the end state even if frames stop arriving mid-reveal.
        setTimeout(function () {
          reveal(node);
        }, 800);
        return;
      }

      observer.observe(node);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor re-renders a section without its classes; re-scan that subtree.
  document.addEventListener('shopify:section:load', function () {
    init();
  });
})();
